"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { useTranslations } from "@/hooks/useTranslations";

type QuickInvoiceFormData = {
  clientId: string;
  amount: string;
  description: string;
  sendImmediately: boolean;
  dueDate?: string;
};

interface Client {
  id: string;
  name: string;
}

interface QuickInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function QuickInvoiceModal({ isOpen, onClose }: QuickInvoiceModalProps) {
  const router = useRouter();
  const { toast } = useToast();
  const t = useTranslations();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const quickInvoiceSchema = z.object({
    clientId: z.string().min(1, t("quickActions.invoice.clientRequired")),
    amount: z.string().min(1, t("quickActions.invoice.amountRequired")),
    description: z.string().min(1, t("quickActions.invoice.descriptionRequired")),
    sendImmediately: z.boolean(),
    dueDate: z.string().optional(),
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<QuickInvoiceFormData>({
    resolver: zodResolver(quickInvoiceSchema),
    defaultValues: {
      clientId: "",
      amount: "",
      description: "",
      sendImmediately: false,
      dueDate: "",
    },
  });

  const clientId = watch("clientId");
  const sendImmediately = watch("sendImmediately");

  // Fetch clients when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsLoadingData(true);
      fetch("/api/clients")
        .then((res) => {
          if (!res.ok) throw new Error("Failed to fetch clients");
          return res.json();
        })
        .then((clientsResult) => {
          const clientsData = clientsResult.data || clientsResult; // Handle both new and old format
          setClients(clientsData);
        })
        .catch((error) => {
          console.error("Error loading data:", error);
          toast({
            title: t("quickActions.invoice.toast.error"),
            description: t("quickActions.invoice.toast.loadError"),
            variant: "destructive",
          });
        })
        .finally(() => {
          setIsLoadingData(false);
        });
    }
  }, [isOpen, toast, t]);

  const onSubmit = async (data: QuickInvoiceFormData) => {
    setIsSubmitting(true);

    try {
      const amount = parseFloat(data.amount);
      const subtotal = amount;
      const tax = subtotal * 0.1; // 10% tax
      const total = subtotal + tax;

      // Create a single line item for the invoice
      const lineItems = [
        {
          id: crypto.randomUUID(),
          description: data.description,
          quantity: 1,
          unitPrice: amount,
          type: "labor" as const,
          total: amount,
        },
      ];

      // Calculate due date (30 days from now if not specified)
      const dueDate = data.dueDate
        ? new Date(data.dueDate)
        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);

      const response = await fetch("/api/invoices", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          clientId: data.clientId,
          lineItems,
          subtotal,
          tax,
          total,
          dueDate: dueDate.toISOString().split("T")[0],
          status: "DRAFT",
          notes: "",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create invoice");
      }

      const invoice = await response.json();

      // If sending immediately, call the send endpoint
      if (data.sendImmediately) {
        try {
          const sendResponse = await fetch(`/api/invoices/${invoice.id}/send`, {
            method: "POST",
          });

          if (!sendResponse.ok) {
            throw new Error("Failed to send invoice");
          }

          toast({
            title: t("quickActions.invoice.toast.sent"),
            description: t("quickActions.invoice.toast.sentDescription"),
          });
        } catch (sendError) {
          console.error("Error sending invoice:", sendError);
          toast({
            title: t("quickActions.invoice.toast.created"),
            description: t("quickActions.invoice.toast.createdPartial"),
          });
        }
      } else {
        toast({
          title: t("quickActions.invoice.toast.created"),
          description: t("quickActions.invoice.toast.createdDescription"),
        });
      }

      reset();
      onClose();
      router.push(`/invoices/${invoice.id}`);
      router.refresh();
    } catch (error) {
      console.error("Error creating invoice:", error);
      toast({
        title: t("quickActions.invoice.toast.error"),
        description: t("quickActions.invoice.toast.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t("quickActions.invoice.title")}</DialogTitle>
          <DialogDescription>
            {t("quickActions.invoice.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoadingData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="clientId">
                {t("quickActions.invoice.clientLabel")} <span className="text-error">*</span>
              </Label>
              <Select
                value={clientId}
                onValueChange={(value) => setValue("clientId", value)}
                disabled={isSubmitting}
              >
                <SelectTrigger id="clientId">
                  <SelectValue placeholder={t("quickActions.invoice.selectClient")} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.clientId && (
                <p className="text-sm text-error">{errors.clientId.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">
                  {t("quickActions.invoice.amountLabel")} <span className="text-error">*</span>
                </Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  {...register("amount")}
                  placeholder="0.00"
                  disabled={isSubmitting}
                />
                {errors.amount && (
                  <p className="text-sm text-error">{errors.amount.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueDate">{t("quickActions.invoice.dueDateLabel")}</Label>
                <Input
                  id="dueDate"
                  type="date"
                  {...register("dueDate")}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                {t("quickActions.invoice.descriptionLabel")} <span className="text-error">*</span>
              </Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder={t("quickActions.invoice.descriptionPlaceholder")}
                rows={3}
                disabled={isSubmitting}
              />
              {errors.description && (
                <p className="text-sm text-error">{errors.description.message}</p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="sendImmediately"
                checked={sendImmediately}
                onChange={(e) => setValue("sendImmediately", e.target.checked)}
                disabled={isSubmitting}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="sendImmediately" className="text-sm font-normal cursor-pointer">
                {t("quickActions.invoice.sendImmediately")}
              </Label>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("quickActions.invoice.creating")}
                  </>
                ) : sendImmediately ? (
                  t("quickActions.invoice.createAndSendButton")
                ) : (
                  t("quickActions.invoice.createButton")
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
